(() => {
  'use strict';

  const doc = document;
  const body = doc.body;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const $ = (selector, scope = doc) => scope.querySelector(selector);
  const $$ = (selector, scope = doc) => Array.from(scope.querySelectorAll(selector));

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#039;',
    '"': '&quot;'
  })[character]);

  const formatMoney = (cents, format = '${{amount}}') => {
    const amount = Number(cents || 0);
    const withDelimiter = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number) || number === null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? parts[0] + decimal + parts[1] : parts[0];
    };

    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return `$${withDelimiter(amount)}`;
    let value;
    switch (match[1]) {
      case 'amount_no_decimals': value = withDelimiter(amount, 0); break;
      case 'amount_with_comma_separator': value = withDelimiter(amount, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiter(amount, 0, '.', ','); break;
      default: value = withDelimiter(amount, 2);
    }
    return format.replace(match[0], value);
  };

  const initSmoothScroll = () => {
    if (reduceMotion || !finePointer) return;

    let current = window.scrollY;
    let target = current;
    let frame = 0;
    let animating = false;

    const maxScroll = () => Math.max(0, doc.documentElement.scrollHeight - window.innerHeight);
    const clamp = (value) => Math.max(0, Math.min(maxScroll(), value));
    const stop = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
      animating = false;
      current = window.scrollY;
      target = current;
    };

    const nestedScrollerCanMove = (start, delta) => {
      let element = start instanceof Element ? start : null;
      while (element && element !== body) {
        const styles = window.getComputedStyle(element);
        const scrollable = /(auto|scroll)/.test(styles.overflowY) && element.scrollHeight > element.clientHeight + 1;
        if (scrollable) {
          const canMoveDown = delta > 0 && element.scrollTop + element.clientHeight < element.scrollHeight - 1;
          const canMoveUp = delta < 0 && element.scrollTop > 1;
          if (canMoveDown || canMoveUp) return true;
        }
        element = element.parentElement;
      }
      return false;
    };

    const glide = () => {
      animating = true;
      current += (target - current) * 0.085;
      if (Math.abs(target - current) < 0.45) current = target;
      window.scrollTo({ top: current, left: 0, behavior: 'instant' });

      if (current !== target) {
        frame = window.requestAnimationFrame(glide);
      } else {
        frame = 0;
        animating = false;
      }
    };

    window.addEventListener('wheel', (event) => {
      if (event.defaultPrevented || event.ctrlKey || body.classList.contains('is-locked')) return;
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      const eventElement = event.target instanceof Element ? event.target : null;
      if (eventElement?.closest('input, textarea, select, [contenteditable="true"], model-viewer')) return;

      const unit = event.deltaMode === 1 ? 24 : event.deltaMode === 2 ? window.innerHeight : 1;
      const delta = event.deltaY * unit;
      if (!delta || nestedScrollerCanMove(eventElement, delta)) return;

      event.preventDefault();
      current = animating ? current : window.scrollY;
      target = clamp((animating ? target : current) + delta * 0.56);
      if (!frame) frame = window.requestAnimationFrame(glide);
    }, { passive: false });

    window.addEventListener('scroll', () => {
      if (animating) return;
      current = window.scrollY;
      target = current;
    }, { passive: true });

    window.addEventListener('resize', stop, { passive: true });
    window.addEventListener('pageshow', stop, { passive: true });
    doc.addEventListener('keydown', (event) => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) stop();
    });
    doc.addEventListener('pointerdown', (event) => {
      if (event.clientX >= doc.documentElement.clientWidth - 18) stop();
    });
  };

  const initHeader = () => {
    const header = $('[data-site-header]');
    const menuButton = $('[data-menu-toggle]');
    const nav = $('.site-nav');
    if (!header) return;

    const updateHeader = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });

    if (menuButton && nav) {
      menuButton.addEventListener('click', () => {
        const isOpen = menuButton.getAttribute('aria-expanded') === 'true';
        menuButton.setAttribute('aria-expanded', String(!isOpen));
        nav.classList.toggle('is-open', !isOpen);
      });
      nav.addEventListener('click', (event) => {
        if (!event.target.closest('a')) return;
        menuButton.setAttribute('aria-expanded', 'false');
        nav.classList.remove('is-open');
      });
    }
  };

  const initAdaptiveTheme = () => {
    const sections = $$('[data-theme-section]');
    if (!sections.length || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        body.dataset.pageTheme = entry.target.dataset.themeSection || 'dark';
      });
    }, { rootMargin: '-42% 0px -52% 0px', threshold: 0 });

    sections.forEach((section) => observer.observe(section));
  };

  const initReveals = () => {
    const elements = $$('[data-reveal], [data-reveal-group]');
    if (!elements.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries, instance) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        instance.unobserve(entry.target);
      });
    }, { threshold: 0.16, rootMargin: '0px 0px -8% 0px' });

    elements.forEach((element) => observer.observe(element));
  };

  const initHeroMotion = () => {
    const hero = $('[data-hero]');
    const stage = $('[data-tilt-stage]', hero || doc);
    const layer = $('[data-tilt-layer]', hero || doc);
    if (!hero || !stage || !layer || reduceMotion) return;

    if (finePointer && body.dataset.pointerEffects === 'true') {
      stage.addEventListener('pointermove', (event) => {
        const bounds = stage.getBoundingClientRect();
        const x = (event.clientX - bounds.left) / bounds.width - 0.5;
        const y = (event.clientY - bounds.top) / bounds.height - 0.5;
        layer.style.transform = `rotateY(${x * 5.5}deg) rotateX(${y * -4.2}deg) translateZ(0)`;
      });
      stage.addEventListener('pointerleave', () => { layer.style.transform = ''; });
    }

    let ticking = false;
    const parallax = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const rect = hero.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          const progress = Math.max(0, Math.min(1, -rect.top / rect.height));
          stage.style.transform = `translate3d(0, ${progress * 54}px, 0) scale(${1 - progress * 0.035})`;
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', parallax, { passive: true });
  };

  const initFeatureStory = () => {
    $$('[data-feature-story]').forEach((story) => {
      const chapters = $$('[data-feature-chapter]', story);
      const visual = $('[data-feature-visual]', story);
      const counter = $('[data-feature-counter]', story);
      const caption = $('[data-feature-caption]', story);
      if (!chapters.length || !('IntersectionObserver' in window)) return;

      const activate = (chapter) => {
        chapters.forEach((item) => item.classList.toggle('is-active', item === chapter));
        const index = Number(chapter.dataset.index || 1);
        if (counter) counter.textContent = String(index).padStart(2, '0');
        if (caption) caption.textContent = chapter.dataset.label || '';
        if (visual) visual.classList.toggle('is-shifted', index % 2 === 0);
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) activate(entry.target);
        });
      }, { threshold: 0.5, rootMargin: '-8% 0px -8% 0px' });
      chapters.forEach((chapter) => observer.observe(chapter));
    });
  };

  const initProductGalleries = () => {
    $$('[data-product-gallery]').forEach((gallery) => {
      const buttons = $$('[data-media-button]', gallery);
      const media = $$('[data-media-id]', gallery);
      const activate = (id) => {
        media.forEach((item) => {
          const active = String(item.dataset.mediaId) === String(id);
          item.hidden = !active;
          item.classList.toggle('is-active', active);
        });
        buttons.forEach((button) => button.classList.toggle('is-active', String(button.dataset.mediaButton) === String(id)));
      };
      buttons.forEach((button) => button.addEventListener('click', () => activate(button.dataset.mediaButton)));
      gallery.activateMedia = activate;
    });
  };

  const initProductForms = () => {
    $$('[data-product-section]').forEach((section) => {
      const form = $('[data-product-form]', section);
      const variantsScript = $('[data-product-variants]', section);
      if (!form || !variantsScript) return;

      let variants = [];
      try { variants = JSON.parse(variantsScript.textContent); } catch (error) { return; }
      const optionSelects = $$('[data-option-select]', form);
      const masterSelect = $('[data-variant-id]', form);
      const price = $('[data-product-price]', section);
      const priceInButton = $('[data-add-to-cart-price]', form);
      const addButton = $('[data-add-to-cart]', form);
      const addText = $('[data-add-to-cart-text]', form);
      const gallery = $('[data-product-gallery]', section);
      const quantity = $('[data-quantity-input]', form);

      const updateVariant = () => {
        const selections = optionSelects.map((select) => select.value);
        const variant = variants.find((item) => item.options.every((value, index) => value === selections[index]));
        optionSelects.forEach((select) => {
          const label = select.closest('.variant-option');
          const value = label && $('[data-option-value]', label);
          if (value) value.textContent = select.value;
        });

        if (!variant) {
          addButton.disabled = true;
          addText.textContent = 'Unavailable';
          return;
        }

        masterSelect.value = variant.id;
        const formattedPrice = formatMoney(variant.price, window.shezaloMoneyFormat);
        if (price) price.textContent = formattedPrice;
        if (priceInButton) priceInButton.textContent = formattedPrice;
        addButton.disabled = !variant.available;
        addText.textContent = variant.available ? 'Add to cart' : 'Sold out';
        if (variant.featured_media && gallery && typeof gallery.activateMedia === 'function') gallery.activateMedia(variant.featured_media.id);

        if (body.classList.contains('template-product') && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      };

      optionSelects.forEach((select) => select.addEventListener('change', updateVariant));
      $('[data-quantity-minus]', form)?.addEventListener('click', () => { quantity.value = Math.max(1, Number(quantity.value || 1) - 1); });
      $('[data-quantity-plus]', form)?.addEventListener('click', () => { quantity.value = Number(quantity.value || 1) + 1; });

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (addButton.disabled) return;
        addButton.classList.add('is-loading');
        const previousText = addText.textContent;
        addText.textContent = 'Adding…';

        try {
          const response = await fetch(window.shezaloRoutes.cartAdd + '.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          if (!response.ok) throw new Error('Unable to add item');
          await refreshCart();
          openCart();
          addText.textContent = 'Added';
          window.setTimeout(() => { addText.textContent = previousText; }, 1200);
        } catch (error) {
          addText.textContent = 'Try again';
          window.setTimeout(() => { addText.textContent = previousText; }, 1800);
        } finally {
          addButton.classList.remove('is-loading');
        }
      });
    });
  };

  const cartShell = $('[data-cart-shell]');
  const cartItems = $('[data-cart-items]');
  const cartFooter = $('[data-cart-footer]');

  const renderCart = (cart) => {
    $$('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
    if (!cartShell || !cartItems || !cartFooter) return;
    if (!cart.items.length) {
      cartItems.innerHTML = '<div class="cart-drawer__empty">Your cart is empty</div>';
      cartFooter.hidden = true;
      return;
    }

    cartItems.innerHTML = cart.items.map((item, index) => `
      <article class="drawer-item">
        ${item.image ? `<img src="${escapeHtml(item.image)}&width=220" alt="${escapeHtml(item.product_title)}" width="84" height="84">` : '<div></div>'}
        <div>
          <h3>${escapeHtml(item.product_title)}</h3>
          ${item.variant_title ? `<p>${escapeHtml(item.variant_title)} · Qty ${item.quantity}</p>` : `<p>Qty ${item.quantity}</p>`}
          <strong>${formatMoney(item.final_line_price, window.shezaloMoneyFormat)}</strong>
        </div>
        <button class="drawer-item__remove" type="button" data-cart-remove="${index + 1}">Remove</button>
      </article>
    `).join('');
    $('[data-cart-total]', cartFooter).textContent = formatMoney(cart.total_price, window.shezaloMoneyFormat);
    cartFooter.hidden = false;

    $$('[data-cart-remove]', cartItems).forEach((button) => {
      button.addEventListener('click', async () => {
        button.disabled = true;
        await fetch(window.shezaloRoutes.cartChange + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(button.dataset.cartRemove), quantity: 0 })
        });
        await refreshCart();
      });
    });
  };

  const refreshCart = async () => {
    const response = await fetch(window.shezaloRoutes.cartJson, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    renderCart(await response.json());
  };

  const openCart = () => {
    if (!cartShell) return;
    cartShell.hidden = false;
    body.classList.add('is-locked');
    window.requestAnimationFrame(() => {
      cartShell.classList.add('is-open');
      $('#CartDrawer')?.focus();
    });
  };

  const closeCart = () => {
    if (!cartShell) return;
    cartShell.classList.remove('is-open');
    body.classList.remove('is-locked');
    window.setTimeout(() => { cartShell.hidden = true; }, 500);
  };

  const initCart = () => {
    $$('[data-cart-open]').forEach((button) => button.addEventListener('click', async () => { await refreshCart(); openCart(); }));
    $$('[data-cart-close]').forEach((button) => button.addEventListener('click', closeCart));
    doc.addEventListener('keydown', (event) => { if (event.key === 'Escape' && cartShell?.classList.contains('is-open')) closeCart(); });
  };

  const initMobileBuyBar = () => {
    if ($('.mobile-buy-bar')) body.classList.add('has-mobile-buy-bar');
  };

  const initPageTransitions = () => {
    if (reduceMotion || body.dataset.pageTransitions !== 'true') return;
    const overlay = $('.page-transition');
    if (!overlay) return;
    doc.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.target || link.hasAttribute('download') || link.dataset.noTransition !== undefined) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.protocol === 'mailto:' || url.protocol === 'tel:') return;
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
      if (url.pathname.includes('/checkout')) return;
      event.preventDefault();
      overlay.classList.add('is-entering');
      window.setTimeout(() => { window.location.href = url.href; }, 430);
    });
    window.addEventListener('pageshow', () => overlay.classList.remove('is-entering'));
  };

  initSmoothScroll();
  initHeader();
  initAdaptiveTheme();
  initReveals();
  initHeroMotion();
  initFeatureStory();
  initProductGalleries();
  initProductForms();
  initCart();
  initMobileBuyBar();
  initPageTransitions();
})();
